/**
 * API Error Handling Utilities for Delivery Options
 *
 * Re-uses app-wide `getApiErrorMessage` (AllExceptionsFilter `errorMsg`, Nest `message`).
 */

import { getApiErrorMessage } from "../../../utils/apiErrorMessage";

/**
 * Standardized API error response format from the backend
 * Used by AllExceptionsFilter
 */
export interface ApiErrorResponse {
  statusCode: number;
  errorMsg: string | string[];
  timestamp: string; // ISO 8601 format
  path: string;
}

/**
 * Type guard to check if an error has the expected Axios error structure
 */
function isAxiosError(
  error: unknown
): error is { response?: { data?: ApiErrorResponse } } {
  return typeof error === "object" && error !== null && "response" in error;
}

/**
 * Extracts a displayable error message from an API error response
 *
 * @param error - The caught error object (typically AxiosError)
 * @param fallbackMessage - Message to use if errorMsg is not available
 * @returns A string suitable for display to the user
 */
export function extractApiErrorMessage(
  error: unknown,
  fallbackMessage: string = "Something went wrong, please try again.",
): string {
  return getApiErrorMessage(error, fallbackMessage);
}

/**
 * Logs API error details to console for debugging
 * Includes statusCode, errorMsg, timestamp, and path when available
 *
 * @param error - The caught error object
 * @param context - Additional context string (e.g., "fetchDeliveryOptions")
 */
export function logApiError(error: unknown, context: string): void {
  console.error(`[${context}] API Error:`, {
    context,
    ...(isAxiosError(error) && error.response?.data
      ? {
          statusCode: error.response.data.statusCode,
          errorMsg: error.response.data.errorMsg,
          timestamp: error.response.data.timestamp,
          path: error.response.data.path,
        }
      : { error }),
  });
}
